<template>
  <div class="text-gray-800 tabular-nums">
    <div class="mb-3 flex flex-wrap items-center gap-x-5 gap-y-2">
      <div class="flex items-center gap-2">
        <span class="text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-500">Show</span>
        <div class="inline-flex overflow-hidden rounded-md border border-gray-300 text-[13px]">
          <button
            v-for="f in FILTERS"
            :key="f[0]"
            class="px-2.5 py-1"
            :class="filter === f[0] ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
            @click="filter = f[0]"
          >{{ f[1] }}</button>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-500">Sort</span>
        <div class="inline-flex overflow-hidden rounded-md border border-gray-300 text-[13px]">
          <button
            v-for="s in SORTS"
            :key="s[0]"
            class="px-2.5 py-1"
            :class="sort === s[0] ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
            @click="sort = s[0]"
          >{{ s[1] }}</button>
        </div>
      </div>
      <span v-if="!loading && rows.length" class="ml-auto text-[11px] text-gray-500">{{ rows.length }} practices · {{ totalPrioritized }} prioritized</span>
    </div>

    <div v-if="loading" class="py-10 text-center text-[13px] text-gray-500">Loading…</div>
    <div v-else-if="!entity_id" class="py-10 text-center text-[13px] text-gray-500">No linked entity for this user.</div>
    <div v-else-if="!rows.length" class="py-10 text-center text-[13px] text-gray-500">{{ emptyMessage }}</div>

    <div v-else class="overflow-hidden rounded-lg border border-gray-200">
      <table class="w-full border-collapse text-[13px]">
        <thead>
          <tr class="border-b border-gray-200 bg-gray-50 text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-500">
            <th class="py-2 pl-4 pr-2 text-left">Practice</th>
            <th class="px-2 py-2 text-right">Open</th>
            <th class="px-2 py-2 text-right">High</th>
            <th class="px-2 py-2 text-right">Actions</th>
            <th class="px-2 py-2 text-right">Priority</th>
            <th class="px-2 pr-4 py-2 text-right">Next</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="p in rows" :key="p.id">
            <tr class="cursor-pointer border-b border-gray-100 hover:bg-gray-50" @click="toggle(p.id)">
              <td class="py-2 pl-4 pr-2">
                <div class="flex items-center gap-2">
                  <span class="w-3 flex-none text-[11px] text-gray-400">{{ openRows[p.id] ? '▾' : '▸' }}</span>
                  <span class="sensitive font-medium text-gray-900">{{ p.name }}</span>
                </div>
              </td>
              <td class="px-2 py-2 text-right font-semibold text-gray-900">{{ p.c_open }}</td>
              <td class="px-2 py-2 text-right font-semibold" :class="p.c_high ? 'text-red-600' : 'text-gray-300'">{{ p.c_high }}</td>
              <td class="px-2 py-2 text-right font-semibold text-gray-900">{{ p.a_open }}</td>
              <td class="px-2 py-2 text-right font-semibold" :class="p.prioritized_count ? 'text-amber-600' : 'text-gray-300'">{{ p.prioritized_count }}</td>
              <td class="px-2 pr-4 py-2 text-right whitespace-nowrap">
                <span class="inline-flex rounded px-1.5 py-0.5 text-[11px] font-semibold" :class="dueClass(p)">{{ dueLabel(p) }}</span>
              </td>
            </tr>
            <tr v-if="openRows[p.id]" class="bg-gray-50/50">
              <td colspan="6" class="px-4 py-1">
                <div v-if="!displayItems(p).length" class="py-2 text-[12px] text-gray-400">Nothing {{ filter === 'priority' ? 'prioritized' : 'open' }} here.</div>
                <router-link
                  v-for="item in displayItems(p)"
                  :key="item.id"
                  :to="{ path: `/entity/${p.id}/engagement-tracker`, query: { node: item.id } }"
                  class="flex items-start gap-2 border-b border-gray-100 py-2 no-underline last:border-0 hover:bg-white"
                >
                  <font-awesome-icon :icon="['fas', 'star']" class="mt-0.5 text-[12px]" :class="item.prioritized ? 'text-amber-400' : 'text-gray-200'" />
                  <div class="min-w-0 flex-1">
                    <span class="sensitive block font-medium leading-snug text-gray-800" data-demo-blur>{{ item.title }}</span>
                    <div class="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
                      <span class="font-semibold uppercase" :class="item.kind === 'constraint' ? 'text-amber-600' : 'text-blue-600'">{{ item.kind }}</span>
                      <span v-if="item.severity === 'high'" class="rounded bg-red-50 px-1.5 py-0.5 font-semibold text-red-700">High</span>
                      <span v-if="item.overdue" class="rounded bg-red-50 px-1.5 py-0.5 font-semibold text-red-700">Overdue</span>
                      <span v-if="item.owner" class="sensitive">{{ item.owner }}</span>
                      <span v-if="item.initiative">{{ titleize(item.initiative) }}</span>
                    </div>
                  </div>
                </router-link>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import { getNextMeetings } from '@/services/api/engagementGraph';

library.add(faStar);

const FILTERS = [['all', 'All'], ['priority', 'Priority only']];
const SORTS = [['upcoming', 'Upcoming'], ['name', 'Alphabetical']];
const ACRONYMS = new Set(['awv', 'hcc', 'tcm', 'toc', 'cdi', 'npi', 'tin', 'emr', 'ed', 'er', 'pcp', 'dme', 'p4p', 'aco']);
const titleize = k => (k || '—').split(/[\s_]+/)
  .map(w => ACRONYMS.has(w.toLowerCase()) ? w.toUpperCase() : (w.charAt(0).toUpperCase() + w.slice(1)))
  .join(' ');

export default {
  name: 'MyPractices',
  components: { FontAwesomeIcon },
  props: {
    entity_id: { type: Number, default: null },
    sectionId: { type: String, default: null },
    expanded: { type: Boolean, default: true },
    fullWidth: { type: Boolean, default: false },
  },
  data() {
    return { loading: true, practices: [], filter: 'all', sort: 'upcoming', openRows: {}, FILTERS, SORTS };
  },
  computed: {
    rows() {
      let list = this.practices.map(p => {
        const items = p.items || [];
        return {
          ...p,
          c_open: items.filter(i => i.kind === 'constraint').length,
          c_high: items.filter(i => i.kind === 'constraint' && i.severity === 'high').length,
          a_open: items.filter(i => i.kind === 'action').length,
          prioritized_count: p.prioritized_count ?? items.filter(i => i.prioritized).length,
        };
      });
      if (this.filter === 'priority') list = list.filter(p => p.prioritized_count > 0);
      if (this.sort === 'name') list = [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      return list;
    },
    totalPrioritized() {
      return this.rows.reduce((n, p) => n + p.prioritized_count, 0);
    },
    emptyMessage() {
      if (this.filter === 'priority') return 'Nothing prioritized for your practices yet.';
      return 'No open work at the practices you manage.';
    },
  },
  watch: {
    entity_id() { this.load(); },
  },
  mounted() { this.load(); },
  methods: {
    titleize,
    async load() {
      if (!this.entity_id) { this.practices = []; this.loading = false; return; }
      this.loading = true;
      const requested = this.entity_id;
      const data = await getNextMeetings(requested);
      if (requested !== this.entity_id) return;
      this.practices = data.practices || [];
      this.loading = false;
    },
    displayItems(p) {
      return this.filter === 'priority' ? (p.items || []).filter(i => i.prioritized) : (p.items || []);
    },
    toggle(id) { this.openRows[id] = !this.openRows[id]; },
    dueLabel(p) {
      if (p.lapsed) return 'Lapsed';
      if (p.next_scheduled) return `Scheduled ${this.fmtDate(p.next_scheduled)}`;
      if (p.days_until_due === null) return 'No meetings yet';
      if (p.days_until_due < 0) return `Due ${-p.days_until_due}d ago`;
      if (p.days_until_due === 0) return 'Due today';
      if (p.days_until_due <= 7) return `Due in ${p.days_until_due}d`;
      return `Due ${this.fmtDate(p.next_due)}`;
    },
    dueClass(p) {
      if (p.lapsed) return 'bg-red-50 text-red-700';
      if (p.days_until_due !== null && p.days_until_due <= 7) return 'bg-blue-50 text-blue-700';
      return 'bg-gray-100 text-gray-600';
    },
    fmtDate(d) {
      return d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
    },
  },
};
</script>
